import { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { Smartphone, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface ZehMotocaAppPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  previewUrl: string | null;
  userLabel?: string;
}

export function ZehMotocaAppPreviewModal({
  isOpen,
  onClose,
  previewUrl,
  userLabel,
}: ZehMotocaAppPreviewModalProps) {
  const titleId = useId();

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'zehmotoca-preview-closed') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      window.addEventListener('message', handleMessage);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('message', handleMessage);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !previewUrl) return null;

  const iframeSrc = previewUrl.startsWith('http')
    ? previewUrl
    : `${window.location.origin}${previewUrl}`;

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" role="presentation">
      <div className="fixed inset-0 bg-foreground/50 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative z-[1] flex w-full max-w-[420px] flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-soft-xl"
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border/70 px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Smartphone className="h-4 w-4 shrink-0 text-primary" />
              <h3 id={titleId} className="font-heading text-sm font-semibold truncate">
                Visualização do app
              </h3>
            </div>
            {userLabel && (
              <p className="mt-0.5 text-xs text-muted-foreground truncate">{userLabel}</p>
            )}
            <p className="mt-1 text-[11px] text-muted-foreground">
              Modo suporte - não é permitido abrir agendamentos por aqui =].
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 shrink-0" aria-label="Fechar">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex justify-center bg-zinc-900 px-4 py-5">
          <div className="relative w-[320px] rounded-[2rem] border-[6px] border-zinc-800 bg-black p-2 shadow-2xl">
            <div className="absolute left-1/2 top-2 z-10 h-4 w-24 -translate-x-1/2 rounded-full bg-zinc-800" />
            <div className="overflow-hidden rounded-[1.4rem] bg-[#FAFAFA]">
              <iframe
                title="Preview Zeh Motoca"
                src={iframeSrc}
                className="h-[640px] w-full border-0 bg-white"
              />
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
